import { ReplaySubject, Subject } from 'rxjs';
import { LanguageService } from 'services/language-service';
import { CONVERSATIONS_EXPORT_TIMEOUT_MS } from 'shared/constants/conversations';
import { EVALUATION_EXPORT_TIMEOUT_MS } from 'shared/constants/evaluation';
import { AUTHENTICATED_GUEST_SMART_ASSISTANT_ID } from 'shared/constants/guest-smart-assistant';
import { SMART_ASSISTANT_TIMEOUT } from 'shared/constants/smart-assistant';
import {
  AskSmartAssistantDto,
  ChatAttachment,
  SmartAssistantMessageEvent,
  TranscribedAudioAttachment
} from 'shared/interfaces/ChatMessage';
import {
  Conversation,
  ExportConversationsDto,
  GetSmartAssistantConversationsQueryDto
} from 'shared/interfaces/Conversation';
import { Evaluation, ExportEvaluationDto } from 'shared/interfaces/Evaluation';
import { ExportSmartAssistantLeadsDto, GetAllSmartAssistantLeadsDto, Lead } from 'shared/interfaces/Lead';
import { CreateGuestAccessTokenMetadataDto } from 'shared/interfaces/livekit/CreateAccessToken';
import {
  GuestSmartAssistantData,
  GuestSmartAssistantDataWithIntegrations,
  UploadAttachmentDto,
  ValidateGuestPasswordDto
} from 'shared/interfaces/SmartAssistant';
import { axiosGuestSmartAssistantInstance } from 'shared/utils/axios-instances';
import { handleSmartAssistantAnswer, reducePartialResponses } from 'shared/utils/smart-assistant';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const guestSmartAssistantBaseEndpoint = '/guest-smart-assistant';
const guestSmartAssistantAuthBaseEndpoint = '/guest-smart-assistant/auth';
const guestSmartAssistantDashboardBaseEndpoint = '/guest-smart-assistant/dashboard';
const guestSmartAssistantInteractBaseEndpoint = '/guest-smart-assistant/interact';

export const GuestSmartAssistantService = {
  // Auth endpoints
  validatePassword: async (validateGuestPasswordDto: ValidateGuestPasswordDto): Promise<void> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'post',
      url: `${guestSmartAssistantAuthBaseEndpoint}/validate-password`,
      data: validateGuestPasswordDto
    });

    setAuthenticatedSession(
      response.data.accessToken,
      response.data.refreshToken,
      validateGuestPasswordDto.smartAssistantId
    );
  },
  refreshAccessToken: async (smartAssistantId: string): Promise<void> => {
    const refreshToken = GuestSmartAssistantService.getRefreshToken(smartAssistantId);
    const response = await axiosGuestSmartAssistantInstance({
      method: 'post',
      url: `${guestSmartAssistantAuthBaseEndpoint}/refresh-token`,
      data: { refreshToken }
    });

    setAuthenticatedSession(response.data.accessToken, response.data.refreshToken, smartAssistantId);
  },
  // Base endpoints
  checkAccessibility: async (smartAssistantId: string, type: 'dashboard' | 'base' = 'base'): Promise<boolean> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'get',
      url: `${
        type === 'dashboard' ? guestSmartAssistantDashboardBaseEndpoint : guestSmartAssistantBaseEndpoint
      }/access-check/${smartAssistantId}`
    });

    return response.data;
  },
  // Interact endpoints
  getGuestSmartAssistantData: async (
    smartAssistantId: string,
    conversationId?: string
  ): Promise<GuestSmartAssistantData> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'get',
      url: `${guestSmartAssistantInteractBaseEndpoint}/${smartAssistantId}`,
      params: { conversationId }
    });

    return response.data;
  },
  askSmartAssistant: async (
    askSmartAssistantDto: AskSmartAssistantDto,
    eventSubject?: Subject<SmartAssistantMessageEvent>,
    partialResponseSubject?: ReplaySubject<SmartAssistantMessageEvent>
  ): Promise<SmartAssistantMessageEvent[]> => {
    let accumulatedMessageEvents: SmartAssistantMessageEvent[] = [];
    let caughtError;

    await axiosGuestSmartAssistantInstance({
      method: 'post',
      url: `${guestSmartAssistantInteractBaseEndpoint}/ask`,
      onDownloadProgress(progressEvent) {
        try {
          accumulatedMessageEvents = handleSmartAssistantAnswer(
            progressEvent,
            accumulatedMessageEvents,
            eventSubject,
            partialResponseSubject
          );
        } catch (e) {
          caughtError = e;
        }
      },
      data: askSmartAssistantDto,
      timeout: SMART_ASSISTANT_TIMEOUT
    });

    if (caughtError) throw caughtError;

    return reducePartialResponses(accumulatedMessageEvents);
  },
  createGuestAccessTokenForNewRoom: async (createGuestAccessTokenMetadataDto: CreateGuestAccessTokenMetadataDto) => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'post',
      url: `${guestSmartAssistantInteractBaseEndpoint}/livekit/access-token`,
      data: createGuestAccessTokenMetadataDto
    });

    return response.data as string;
  },
  uploadAttachment: async (uploadAttachmentDto: UploadAttachmentDto): Promise<ChatAttachment> => {
    const fileData = new FormData();
    fileData.append('file', uploadAttachmentDto.file);
    fileData.append('conversationId', uploadAttachmentDto.conversationId);
    fileData.append('type', uploadAttachmentDto.type);

    const response = await axiosGuestSmartAssistantInstance({
      method: 'post',
      url: `${guestSmartAssistantInteractBaseEndpoint}/upload-attachment/${uploadAttachmentDto.smartAssistantId}`,
      data: fileData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  transcribeAudioAttachment: async ({
    file,
    smartAssistantId
  }: {
    file: File;
    smartAssistantId: string;
  }): Promise<TranscribedAudioAttachment> => {
    const fileData = new FormData();
    fileData.append('file', file);

    const response = await axiosGuestSmartAssistantInstance({
      method: 'post',
      url: `${guestSmartAssistantInteractBaseEndpoint}/transcribe-audio-attachment/${smartAssistantId}`,
      data: fileData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },
  // Dashboard endpoints
  getGuestSmartAssistantDataWithIntegrations: async (
    smartAssistantId: string
  ): Promise<GuestSmartAssistantDataWithIntegrations> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'get',
      url: `${guestSmartAssistantDashboardBaseEndpoint}/${smartAssistantId}`,
      params: { withIntegrations: true }
    });

    return response.data;
  },
  exportConversations: async (exportConversationsDto: ExportConversationsDto): Promise<ArrayBuffer> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'get',
      responseType: 'arraybuffer',
      url: `${guestSmartAssistantDashboardBaseEndpoint}/conversation/export/${exportConversationsDto.smartAssistantId}`,
      params: {
        ...exportConversationsDto.query,
        exportType: exportConversationsDto.type,
        locale: LanguageService.getCurrentLanguage(),
        timezoneOffsetMs: new Date().getTimezoneOffset() * 60 * 1000 * -1 // -1 because getTimezoneOffset returns positive value for negative offset
      },
      timeout: CONVERSATIONS_EXPORT_TIMEOUT_MS
    });

    return response.data;
  },
  getAllSmartAssistantConversations: async (
    smartAssistantId: string,
    query: GetSmartAssistantConversationsQueryDto
  ): Promise<Conversation[]> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'get',
      url: `${guestSmartAssistantDashboardBaseEndpoint}/conversation/with-smart-assistant/${smartAssistantId}`,
      params: query
    });

    return response.data;
  },
  getSmartAssistantLeads: async (getSmartAssistantLeadsDto: GetAllSmartAssistantLeadsDto): Promise<Lead[]> => {
    const leadsResponse = await axiosGuestSmartAssistantInstance({
      method: 'get',
      url: `${guestSmartAssistantDashboardBaseEndpoint}/lead/with-smart-assistant/${getSmartAssistantLeadsDto.smartAssistantId}`,
      params: getSmartAssistantLeadsDto.getSmartAssistantLeadsQueryDto
    });

    return leadsResponse.data as Lead[];
  },
  exportSmartAssistantLeads: async (exportSmartAssistantLeads: ExportSmartAssistantLeadsDto): Promise<ArrayBuffer> => {
    const response = await axiosGuestSmartAssistantInstance({
      method: 'get',
      responseType: 'arraybuffer',
      url: `${guestSmartAssistantDashboardBaseEndpoint}/lead/export/${exportSmartAssistantLeads.smartAssistantId}`,
      params: {
        ...exportSmartAssistantLeads.query,
        exportType: exportSmartAssistantLeads.type,
        locale: LanguageService.getCurrentLanguage(),
        timezoneOffsetMs: new Date().getTimezoneOffset() * 60 * 1000 * -1 // -1 because getTimezoneOffset returns positive value for negative offset
      }
    });

    return response.data;
  },
  getEvaluationsForSmartAssistant: async (smartAssistantId: string): Promise<Evaluation[]> => {
    const response = await axiosGuestSmartAssistantInstance.get(
      `${guestSmartAssistantDashboardBaseEndpoint}/evaluation/${smartAssistantId}`
    );

    return response.data;
  },
  exportEvaluation: async (evaluationId: string, exportEvaluationDto: ExportEvaluationDto): Promise<ArrayBuffer> => {
    const response = await axiosGuestSmartAssistantInstance.post(
      `${guestSmartAssistantDashboardBaseEndpoint}/evaluation/export/${evaluationId}`,
      exportEvaluationDto,
      {
        responseType: 'arraybuffer',
        timeout: EVALUATION_EXPORT_TIMEOUT_MS
      }
    );
    return response.data;
  },
  // Helper functions
  clearSession: (smartAssistantId: string): void => {
    localStorage.removeItem(`${ACCESS_TOKEN_KEY}_${smartAssistantId}`);
    localStorage.removeItem(`${REFRESH_TOKEN_KEY}_${smartAssistantId}`);
  },
  getAccessToken: (smartAssistantId: string): string | null =>
    localStorage.getItem(`${ACCESS_TOKEN_KEY}_${smartAssistantId}`),

  getRefreshToken: (smartAssistantId: string): string | null =>
    localStorage.getItem(`${REFRESH_TOKEN_KEY}_${smartAssistantId}`),

  isAuthenticated: (smartAssistantId: string): boolean =>
    GuestSmartAssistantService.getAccessToken(smartAssistantId) !== null &&
    GuestSmartAssistantService.getRefreshToken(smartAssistantId) !== null
};

const setAuthenticatedSession = (accessToken: string, refreshToken: string, smartAssistantId: string) => {
  localStorage.setItem(`${ACCESS_TOKEN_KEY}_${smartAssistantId}`, accessToken);
  localStorage.setItem(`${REFRESH_TOKEN_KEY}_${smartAssistantId}`, refreshToken);
  sessionStorage.setItem(AUTHENTICATED_GUEST_SMART_ASSISTANT_ID, smartAssistantId);
};
